import { useState, useEffect } from 'react';
import { useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import { 
  DollarSign, Users, FileText, TrendingUp, Settings,
  LogOut, Menu, X, ChevronDown, LayoutGrid, Bell,
  Search, User, HelpCircle, Home
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

// Product configuration
const PRODUCTS = {
  finance: {
    id: 'finance',
    name: 'MyFinance Orbit',
    shortName: 'Finance',
    icon: DollarSign,
    color: 'emerald',
    bgGradient: 'from-emerald-500 to-green-600',
    lightBg: 'bg-emerald-50',
    textColor: 'text-emerald-600'
  },
  hr: {
    id: 'hr',
    name: 'MyHR Orbit',
    shortName: 'HR',
    icon: Users,
    color: 'blue',
    bgGradient: 'from-blue-500 to-indigo-600',
    lightBg: 'bg-blue-50',
    textColor: 'text-blue-600'
  },
  docs: {
    id: 'docs',
    name: 'MyDoc Orbit',
    shortName: 'Docs',
    icon: FileText,
    color: 'purple',
    bgGradient: 'from-purple-500 to-violet-600',
    lightBg: 'bg-purple-50',
    textColor: 'text-purple-600'
  },
  sales: {
    id: 'sales',
    name: 'MySales Orbit',
    shortName: 'Sales',
    icon: TrendingUp,
    color: 'orange',
    bgGradient: 'from-orange-500 to-amber-600',
    lightBg: 'bg-orange-50',
    textColor: 'text-orange-600'
  }
};

export default function EnterpriseSuiteLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // Get current product from URL
  const currentProductId = location.pathname.split('/')[2]; // /suite/hr -> hr
  const currentProduct = PRODUCTS[currentProductId];

  useEffect(() => {
    const userData = localStorage.getItem('userData');
    const token = localStorage.getItem('authToken');
    
    if (!userData || !token) {
      navigate('/login');
      return;
    }

    const parsedUser = JSON.parse(userData);
    setUser(parsedUser);
    
    // Check if user has access to current product
    if (currentProductId && !parsedUser.products?.some(p => p.id === currentProductId)) {
      navigate('/portal');
    }
  }, [navigate, currentProductId]);

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userData');
    navigate('/login');
  };

  // Get user's accessible products
  const userProducts = user?.products?.filter(p => PRODUCTS[p.id]) || [];

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900 flex">
      {/* Sidebar */}
      <aside className={cn(
        "fixed inset-y-0 left-0 z-50 flex flex-col bg-slate-950 border-r border-slate-800 transition-all duration-300",
        sidebarOpen ? "w-64" : "w-20",
        "hidden lg:flex"
      )}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800">
          {sidebarOpen ? (
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_aurous-ecosystem/artifacts/x28qd6xo_30882183-9DD0-40B4-98FF-3BB81A460020.png"
                alt="Aurous"
                className="h-8 w-auto"
              />
            </Link>
          ) : (
            <Link to="/" className="mx-auto">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                <span className="text-white font-bold">A</span>
              </div>
            </Link>
          )}
          <button 
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400"
          >
            <Menu className="w-5 h-5" />
          </button>
        </div>

        {/* Current Product Badge */}
        {currentProduct && (
          <div className={cn("px-4 py-3 border-b border-slate-800", !sidebarOpen && "px-2")}>
            <div className={cn(
              "flex items-center gap-3 p-3 rounded-xl bg-gradient-to-r",
              currentProduct.bgGradient
            )}>
              <currentProduct.icon className="w-5 h-5 text-white flex-shrink-0" />
              {sidebarOpen && (
                <span className="text-white font-semibold text-sm truncate">
                  {currentProduct.name}
                </span>
              )}
            </div>
          </div>
        )}

        {/* Product Switcher */}
        {userProducts.length > 1 && (
          <div className={cn("px-4 py-3 border-b border-slate-800", !sidebarOpen && "px-2")}>
            {sidebarOpen && <p className="text-xs text-slate-500 mb-2 uppercase tracking-wider">Switch Product</p>}
            <div className="space-y-1">
              {userProducts.map((product) => {
                const prod = PRODUCTS[product.id];
                if (!prod) return null;
                const Icon = prod.icon;
                const isActive = currentProductId === product.id;
                
                return (
                  <Link
                    key={product.id}
                    to={`/suite/${product.id}`}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2 rounded-lg transition-colors",
                      isActive 
                        ? "bg-slate-800 text-white" 
                        : "text-slate-400 hover:bg-slate-800/50 hover:text-white"
                    )}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" />
                    {sidebarOpen && <span className="text-sm">{prod.shortName}</span>}
                  </Link>
                );
              })}
            </div>
          </div>
        )}

        {/* Navigation Links */}
        <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
          {sidebarOpen && <p className="text-xs text-slate-500 mb-2 uppercase tracking-wider">Navigation</p>}
          <Link
            to={`/suite/${currentProductId}`}
            className={cn(
              "flex items-center gap-3 px-3 py-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors",
              location.pathname === `/suite/${currentProductId}` && "bg-slate-800 text-white"
            )}
          >
            <Home className="w-5 h-5" />
            {sidebarOpen && <span className="text-sm">Dashboard</span>}
          </Link>
          <Link
            to="/portal"
            className="flex items-center gap-3 px-3 py-2 rounded-lg text-slate-400 hover:bg-slate-800 hover:text-white transition-colors"
          >
            <LayoutGrid className="w-5 h-5" />
            {sidebarOpen && <span className="text-sm">All Products</span>}
          </Link>
        </nav>

        {/* User Section */}
        <div className={cn("p-4 border-t border-slate-800", !sidebarOpen && "px-2")}>
          <div className={cn(
            "flex items-center gap-3",
            !sidebarOpen && "justify-center"
          )}>
            <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-white font-semibold">
              {user.name?.charAt(0)}
            </div>
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user.name}</p>
                <p className="text-xs text-slate-500 truncate">{user.email}</p>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <aside className="fixed inset-y-0 left-0 w-64 bg-slate-950 border-r border-slate-800">
            {/* Same content as desktop sidebar */}
            <div className="h-16 flex items-center justify-between px-4 border-b border-slate-800">
              <Link to="/" className="flex items-center gap-2">
                <img 
                  src="https://customer-assets.emergentagent.com/job_aurous-ecosystem/artifacts/x28qd6xo_30882183-9DD0-40B4-98FF-3BB81A460020.png"
                  alt="Aurous"
                  className="h-8 w-auto"
                />
              </Link>
              <button 
                onClick={() => setMobileMenuOpen(false)}
                className="p-1.5 rounded-lg hover:bg-slate-800 text-slate-400"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            {/* Product links for mobile */}
            <div className="px-4 py-4 space-y-1">
              {userProducts.map((product) => {
                const prod = PRODUCTS[product.id];
                if (!prod) return null;
                const Icon = prod.icon;
                
                return (
                  <Link
                    key={product.id}
                    to={`/suite/${product.id}`}
                    onClick={() => setMobileMenuOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-3 py-3 rounded-lg transition-colors",
                      currentProductId === product.id 
                        ? "bg-slate-800 text-white" 
                        : "text-slate-400 hover:bg-slate-800/50"
                    )}
                  >
                    <Icon className="w-5 h-5" />
                    <span>{prod.name}</span>
                  </Link>
                );
              })}
            </div>
          </aside>
        </div>
      )}

      {/* Main Content */}
      <main className={cn(
        "flex-1 transition-all duration-300",
        sidebarOpen ? "lg:ml-64" : "lg:ml-20"
      )}>
        {/* Top Header */}
        <header className="h-16 bg-slate-900 border-b border-slate-800 flex items-center justify-between px-4 lg:px-6 sticky top-0 z-40">
          {/* Mobile menu button */}
          <button 
            onClick={() => setMobileMenuOpen(true)}
            className="lg:hidden p-2 rounded-lg hover:bg-slate-800 text-slate-400"
          >
            <Menu className="w-5 h-5" />
          </button>

          {/* Search */}
          <div className="hidden md:flex items-center flex-1 max-w-md mx-4">
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
              <Input 
                placeholder="Search..." 
                className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
              />
            </div>
          </div>

          {/* Right actions */}
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white">
              <Bell className="w-5 h-5" />
            </Button>
            <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white">
              <HelpCircle className="w-5 h-5" />
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 text-slate-300">
                  <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center text-white text-sm font-semibold">
                    {user.name?.charAt(0)}
                  </div>
                  <ChevronDown className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56 bg-slate-800 border-slate-700">
                <DropdownMenuLabel className="text-slate-300">
                  <p className="font-medium">{user.name}</p>
                  <p className="text-xs text-slate-500 font-normal">{user.email}</p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator className="bg-slate-700" />
                <DropdownMenuItem className="text-slate-300 hover:bg-slate-700 cursor-pointer">
                  <User className="w-4 h-4 mr-2" />
                  Profile
                </DropdownMenuItem>
                <DropdownMenuItem className="text-slate-300 hover:bg-slate-700 cursor-pointer">
                  <Settings className="w-4 h-4 mr-2" />
                  Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-slate-700" />
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="text-red-400 hover:bg-red-500/10 cursor-pointer"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page Content - minimal padding for iframe */}
        <div className="p-2 h-[calc(100vh-4rem)]">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
